/**
 * Native SVG radar chart for the wine Stijl stat circle.
 *
 * Scores are read from data attributes: data-fraicheur, data-body,
 * data-complex, data-mineraal (0–5 scale). The polygon animates from the
 * center outward once the stat circle becomes visible.
 *
 * Visibility triggers:
 * - Desktop: section has `is-expanded` + `is-stats-revealed`, circle has
 *   `is-circle-active` (hover/focus).
 * - Mobile: section has `is-expanded`, matching stats slide has `data-active`.
 *
 * Loaded only on wine PDPs (excluded from box template).
 */
import { prefersReducedMotion } from '@theme/utilities';

const VIEWBOX_SIZE = 220;
const CENTER = VIEWBOX_SIZE / 2;
const MAX_RADIUS = 90;
const LEVELS = 5;
const ANIMATION_MS = 650;
const SVG_NS = 'http://www.w3.org/2000/svg';

// -------------------------------------------------------------------------
// SVG helpers
// -------------------------------------------------------------------------

/**
 * Parses a score string into a clamped 0–5 number.
 * @param {string | undefined} value
 * @returns {number}
 */
function clampScore(value) {
  const normalized = String(value ?? '').trim().replace(',', '.');
  const num = Number.parseFloat(normalized);
  if (Number.isNaN(num)) return 0;
  return Math.min(LEVELS, Math.max(0, num));
}

/**
 * Maps a 0–5 score to a radar radius in SVG units.
 * @param {number} score
 * @returns {number}
 */
function scoreToRadius(score) {
  return (score / LEVELS) * MAX_RADIUS;
}

/**
 * Builds the four-axis polygon point string from score values.
 * @param {{ fraicheur: number, body: number, complex: number, mineraal: number }} scores
 * @returns {string}
 */
function buildPolygonPoints(scores) {
  const cx = CENTER;
  const cy = CENTER;
  const northY = cy - scoreToRadius(scores.fraicheur);
  const eastX = cx + scoreToRadius(scores.body);
  const southY = cy + scoreToRadius(scores.complex);
  const westX = cx - scoreToRadius(scores.mineraal);

  return `${cx},${northY} ${eastX},${cy} ${cx},${southY} ${westX},${cy}`;
}

/**
 * @param {string} points
 * @returns {{ x: number, y: number }[]}
 */
function parsePoints(points) {
  return points.split(' ').map((pair) => {
    const [x = 0, y = 0] = pair.split(',').map(Number);
    return { x, y };
  });
}

/**
 * @param {{ x: number, y: number }[]} points
 * @returns {string}
 */
function formatPoints(points) {
  return points.map((point) => `${point.x},${point.y}`).join(' ');
}

/**
 * @param {number} t
 * @returns {number}
 */
function easeOutCubic(t) {
  return 1 - (1 - t) ** 3;
}

// -------------------------------------------------------------------------
// Custom element
// -------------------------------------------------------------------------

class StyleRadarChart extends HTMLElement {
  /** @type {SVGPolygonElement | null} */
  #polygon = null;

  /** @type {string} */
  #targetPoints = '';

  /** @type {number | null} */
  #animationFrame = null;

  /** @type {Element | null} */
  #section = null;

  /** @type {HTMLElement | null} */
  #circleElement = null;

  /** @type {MutationObserver | null} */
  #visibilityObserver = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #playTimer = null;

  /** @type {boolean} */
  #hasPlayed = false;

  // Lifecycle

  connectedCallback() {
    this.#render();
    this.#bindTriggers();

    if (this.#isSlideActive()) {
      requestAnimationFrame(() => this.#schedulePlayWhenVisible());
    }
  }

  disconnectedCallback() {
    this.#cancelAnimation();
    if (this.#playTimer) clearTimeout(this.#playTimer);
    this.#unbindTriggers();
  }

  // SVG render

  /** Builds the grid + polygon SVG from data attributes. */
  #render() {
    const scores = {
      fraicheur: clampScore(this.dataset.fraicheur),
      body: clampScore(this.dataset.body),
      complex: clampScore(this.dataset.complex),
      mineraal: clampScore(this.dataset.mineraal),
    };

    this.#targetPoints = buildPolygonPoints(scores);

    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', `0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`);
    svg.setAttribute('class', 'radar-chart__svg');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');

    const gridGroup = document.createElementNS(SVG_NS, 'g');
    gridGroup.setAttribute('class', 'radar-chart__grid');

    for (let level = 1; level <= LEVELS; level += 1) {
      const circle = document.createElementNS(SVG_NS, 'circle');
      circle.setAttribute('cx', String(CENTER));
      circle.setAttribute('cy', String(CENTER));
      circle.setAttribute('r', String((level / LEVELS) * MAX_RADIUS));
      circle.setAttribute('class', 'radar-chart__grid-circle');
      gridGroup.appendChild(circle);
    }

    const axisV = document.createElementNS(SVG_NS, 'line');
    axisV.setAttribute('x1', String(CENTER));
    axisV.setAttribute('y1', String(CENTER - MAX_RADIUS));
    axisV.setAttribute('x2', String(CENTER));
    axisV.setAttribute('y2', String(CENTER + MAX_RADIUS));
    axisV.setAttribute('class', 'radar-chart__grid-axis');
    gridGroup.appendChild(axisV);

    const axisH = document.createElementNS(SVG_NS, 'line');
    axisH.setAttribute('x1', String(CENTER - MAX_RADIUS));
    axisH.setAttribute('y1', String(CENTER));
    axisH.setAttribute('x2', String(CENTER + MAX_RADIUS));
    axisH.setAttribute('y2', String(CENTER));
    axisH.setAttribute('class', 'radar-chart__grid-axis');
    gridGroup.appendChild(axisH);

    const polygonGroup = document.createElementNS(SVG_NS, 'g');
    polygonGroup.setAttribute('class', 'radar-chart__polygon-group');

    const polygon = document.createElementNS(SVG_NS, 'polygon');
    polygon.setAttribute('class', 'radar-chart__polygon');
    polygonGroup.appendChild(polygon);

    svg.appendChild(gridGroup);
    svg.appendChild(polygonGroup);

    this.replaceChildren(svg);
    this.#polygon = polygon;
    this.#resetPolygon();
  }

  /** Collapses the polygon back to the center point. */
  #resetPolygon() {
    if (!this.#polygon) return;

    const centerPoint = { x: CENTER, y: CENTER };
    this.#polygon.setAttribute('points', formatPoints([centerPoint, centerPoint, centerPoint, centerPoint]));
  }

  // Animation

  /** Cancels any in-progress requestAnimationFrame loop. */
  #cancelAnimation() {
    if (this.#animationFrame != null) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  /** Animates the polygon from center to target scores. */
  #playAnimation(/** @type {(() => void) | undefined} */ onComplete) {
    if (!this.#polygon) return;

    this.#cancelAnimation();

    if (prefersReducedMotion()) {
      this.#polygon.setAttribute('points', this.#targetPoints);
      onComplete?.();
      return;
    }

    const target = parsePoints(this.#targetPoints);
    const origin = { x: CENTER, y: CENTER };
    const start = target.map(() => origin);
    const startTime = performance.now();

    this.#polygon.setAttribute('points', formatPoints(start));

    const tick = (/** @type {number} */ now) => {
      const progress = Math.min(1, (now - startTime) / ANIMATION_MS);
      const eased = easeOutCubic(progress);
      const current = target.map((point, index) => ({
        x: (start[index] ?? origin).x + (point.x - (start[index] ?? origin).x) * eased,
        y: (start[index] ?? origin).y + (point.y - (start[index] ?? origin).y) * eased,
      }));

      this.#polygon?.setAttribute('points', formatPoints(current));

      if (progress < 1) {
        this.#animationFrame = requestAnimationFrame(tick);
      } else {
        this.#animationFrame = null;
        onComplete?.();
      }
    };

    this.#animationFrame = requestAnimationFrame(tick);
  }

  // Visibility

  /** True when the stat circle is expanded and this chart should be shown. */
  #isCircleVisible() {
    const blockId = this.dataset.blockId;
    if (!blockId) return false;

    const section = this.closest('vitis-product-information');
    if (!section?.classList.contains('is-expanded')) return false;

    if (window.matchMedia('(max-width: 989px)').matches) {
      return this.#isSlideActive();
    }

    if (!section.classList.contains('is-stats-revealed')) return false;

    const circle = this.#circleElement ?? section.querySelector(`#VitisStatCircle-${blockId}`);
    return circle?.classList.contains('is-circle-active') ?? false;
  }

  /** True when the matching mobile stats slide is active. */
  #isSlideActive() {
    const blockId = this.dataset.blockId;
    if (!blockId) return false;

    const section = this.closest('vitis-product-information');
    if (!section?.classList.contains('is-expanded')) return false;

    const slideId = `vitis-stat-${blockId}`;
    return Boolean(
      section.querySelector(
        `.vitis-product__stats-mobile [data-slide-id="${slideId}"][data-active="true"]`
      )
    );
  }

  /** Debounced entry point: plays animation once when circle becomes visible. */
  #schedulePlayWhenVisible() {
    if (this.#hasPlayed) {
      this.#polygon?.setAttribute('points', this.#targetPoints);
      return;
    }

    if (!this.#isCircleVisible() || this.#animationFrame != null) return;

    if (this.#playTimer) clearTimeout(this.#playTimer);
    this.#playTimer = window.setTimeout(() => {
      this.#playTimer = null;
      if (!this.#isCircleVisible() || this.#hasPlayed || this.#animationFrame != null) return;

      this.#playAnimation(() => {
        this.#hasPlayed = true;
      });
    }, 100);
  }

  /** Reacts to section class and slide attribute changes. */
  #handleVisibilityChange() {
    if (this.#isCircleVisible() || this.#isSlideActive()) {
      this.#schedulePlayWhenVisible();
    } else if (!this.#hasPlayed) {
      this.#resetPolygon();
    }
  }

  // Observers

  /** Watches section state to trigger the animation. */
  #bindTriggers() {
    const blockId = this.dataset.blockId;
    if (!blockId) return;

    const section = this.closest('vitis-product-information');
    if (!section) return;
    this.#section = section;

    this.#circleElement = section.querySelector(`#VitisStatCircle-${blockId}`);

    this.#visibilityObserver = new MutationObserver(() => {
      this.#handleVisibilityChange();
    });
    this.#visibilityObserver.observe(section, {
      attributes: true,
      attributeFilter: ['class', 'data-active'],
      subtree: true,
    });

    if (this.#circleElement) {
      this.#visibilityObserver.observe(this.#circleElement, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    requestAnimationFrame(() => this.#handleVisibilityChange());
  }

  #unbindTriggers() {
    this.#visibilityObserver?.disconnect();
    if (this.#playTimer) clearTimeout(this.#playTimer);

    this.#section = null;
    this.#circleElement = null;
    this.#visibilityObserver = null;
    this.#playTimer = null;
  }
}

if (!customElements.get('style-radar-chart')) {
  customElements.define('style-radar-chart', StyleRadarChart);
}
